/**
 * The Property section — `docs/new-request.md` §5.4, §7, §16.3; Boards 3, 4,
 * 5, 6 and 14.
 *
 * Three shapes, one section:
 *
 * - **Typing one** — the address box (§7.1), and once the address is settled
 *   its map, Type, Name and Zone · Branch (§7.3–§7.5); Board 5 A's band when
 *   the address is already a property (§7.2), and *Unit or lot* after *It's
 *   another unit or lot*.
 * - **A chosen contact's places** — radio cards, the most recently worked one
 *   chosen, and *A new property for <first name>* (§5.4, Board 3 D).
 * - **Chosen** — the card with **Change**, and *<n> jobs here · last: …*.
 *
 * `mode: 'card'` is the folded, ready look of Board 6: the heading with its
 * tick and the card (**Edit** for a typed one, with its **New** tag; **Change**
 * for a chosen one).
 *
 * The server decides the zone, the branch and whether the address is taken;
 * the name follows `propertyName()` while it is **auto**. Nothing here works
 * out a distance.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  PROPERTY_TYPES, propertyName as suggestName, type KnownContact, type KnownProperty,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { humanize } from '../components/primitives.js'
import { CheckGlyph } from '../components/keycap.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { TextInput, controlClass } from '../components/form/index.js'
import {
  IconCheck, IconChevronRight, IconPin, IconPlus, IconWarn,
} from '../frame/icons.js'
import {
  addressReady, propertyStatus, type SheetState, type TypedProperty,
} from './model.js'
import { useRequestSheet } from './sheet-context.js'
import { ReplaceAsk, SectionHeading } from './section-frame.js'
import { KeySelect } from './key-select.js'
import { PlaceMark } from './search-box.js'
import {
  AddressBox, FieldLabel, UNIT_HINT, addressLine, changeProperty, existingShown, freshProperty,
  typedByHand,
} from './address-box.js'

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
const countWord = (n: number): string => NUMBER_WORDS[n] ?? String(n)
const jobsWord = (n: number): string => `${n} job${n === 1 ? '' : 's'}`

// ---------------------------------------------------------------------------
// Summary for the folded line
// ---------------------------------------------------------------------------

/**
 * The words on a folded Property line (§5.0): the name and where it is when
 * there is one, else what was typed into the address box.
 */
export function propertySummary(state: SheetState): string {
  const property = state.property
  if (property.kind === 'chosen') {
    const known = property.property
    return [known.name, `${known.line1}, ${known.city}`].join(' · ')
  }
  if (property.kind === 'none') return ''
  const typed = property.property
  const address = typed.address
  if (!addressReady(address)) return address.line1.trim()
  const zone = zoneName(typed)
  return [typed.name.trim() || null, addressLine(address, true), typed.type,
    zone ? `${zone} zone` : null].filter(Boolean).join(' · ')
}

function zoneName(property: TypedProperty): string | null {
  if (!property.zoneId) return null
  return property.check?.zones.find((zone) => zone.id === property.zoneId)?.name ?? null
}

function branchName(property: TypedProperty): string | null {
  if (!property.orgUnitId) return null
  return property.check?.branches.find((branch) => branch.id === property.orgUnitId)?.name ?? null
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export function PropertySection({ mode }: { mode: 'open' | 'card' }) {
  const api = useRequestSheet()
  const { state } = api
  const ready = propertyStatus(state) === 'ready'
  const contact = state.contact.kind === 'chosen' ? state.contact.contact : null
  const places = contact && contact.properties.length >= 2 && state.propertyFromContact
    ? contact.properties : null

  useAutoName()

  if (mode === 'card') {
    return (
      <div>
        <SectionHeading section="property" ready={ready} />
        <ReplaceAsk section="property" />
        {state.property.kind === 'chosen'
          ? <ChosenCard property={state.property.property} />
          : state.property.kind === 'new' ? <TypedCard property={state.property.property} /> : null}
      </div>
    )
  }

  const sub = places && contact
    ? `${contact.firstName} has ${countWord(places.length)}`
    : state.property.kind === 'chosen' ? undefined : 'where the work is'

  return (
    <div>
      <SectionHeading section="property" ready={ready} sub={sub} />
      <ReplaceAsk section="property" />
      {places && contact && <PlacesOf contact={contact} />}
      {state.property.kind === 'chosen' && !places && (
        <>
          <ChosenCard property={state.property.property} />
          <JobsHere property={state.property.property} />
        </>
      )}
      {state.property.kind !== 'chosen' && (!places || state.property.kind === 'new') && (
        <div className={places ? 'mt-3.5' : undefined}>
          <TypedFields />
        </div>
      )}
    </div>
  )
}

/** The address, then everything an address decides (§7.1–§7.5). */
function TypedFields() {
  const api = useRequestSheet()
  const property = api.state.property.kind === 'new' ? api.state.property.property : null
  const address = property?.address
  const settled = Boolean(address && (address.verified || typedByHand(address)))
  const existing = property ? existingShown(property) : null
  const gap = api.phone ? 'h-2.5' : 'h-3'

  return (
    <div>
      <AddressBox />
      {property && existing && <ExistingBand property={property} />}
      {property && settled && !existing && (
        <>
          <div className={gap} />
          <TypeChips property={property} />
          <div className={gap} />
          <NameField property={property} />
          <div className={gap} />
          <ZoneAndBranch property={property} />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The name follows the rules while it is auto (§7.4)
// ---------------------------------------------------------------------------

function useAutoName() {
  const { state, update } = useRequestSheet()
  const property = state.property.kind === 'new' ? state.property.property : null
  const contact = state.contact
  const first = contact.kind === 'new' ? contact.fields.firstName
    : contact.kind === 'chosen' ? contact.contact.firstName : null
  const last = contact.kind === 'new' ? contact.fields.lastName
    : contact.kind === 'chosen' ? contact.contact.lastName : null
  const company = contact.kind === 'new' ? contact.fields.company
    : contact.kind === 'chosen' ? contact.contact.company : null
  const theirs = contact.kind === 'chosen' ? contact.contact.properties : []
  const hasResidence = theirs.some((place) =>
    (place.type ?? '').toLowerCase() === 'residential' || /\bResidence\b/.test(place.name))

  const wanted = property && property.nameIsAuto
    ? suggestName({
      type: property.type, firstName: first, lastName: last, company,
      line1: property.address.line1, contactHasResidence: hasResidence,
      companyPropertyNames: theirs.map((place) => place.name),
    })
    : null

  useEffect(() => {
    if (wanted === null) return
    update((current) => {
      if (current.property.kind !== 'new') return current
      const typed = current.property.property
      if (!typed.nameIsAuto || typed.name === wanted) return current
      return { ...current, property: { kind: 'new', property: { ...typed, name: wanted } } }
    })
  }, [wanted, update])
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function CardShell({ tile, title, tag, sub, action }: {
  tile: ReactNode
  title: string
  tag?: ReactNode
  sub: string
  action: ReactNode
}) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-accent-line bg-green-band-bg px-3.5 py-3">
      {tile}
      {/* The second line wraps rather than cutting the town off (Boards 3 and 6: "… West Shore · / Harrisburg"). */}
      <div className="flex min-w-0 flex-col gap-[3px] leading-tight">
        <div className="flex items-center gap-1.5">
          <b className="truncate text-[14.5px] font-bold text-text">{title}</b>
          {tag}
        </div>
        <span className="text-[12.5px] leading-[15px] text-text-muted">{sub}</span>
      </div>
      <span className="flex-1" />
      {action}
    </div>
  )
}

function LinkButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="h-[30px] shrink-0 rounded-button px-1.5 text-[12.5px] font-semibold text-accent hover:underline">
      {children}
    </button>
  )
}

/** A chosen property (Board 3 B): *street, city · type · zone · branch* and **Change**. */
function ChosenCard({ property }: { property: KnownProperty }) {
  const api = useRequestSheet()
  return (
    <CardShell
      tile={<PlaceMark seed={property.id} colour={property.zoneColour} size={38} title={property.zoneName ?? undefined} />}
      title={property.name}
      sub={[`${property.line1}, ${property.city}`, property.type, property.zoneName, property.branchName]
        .filter(Boolean).join(' · ')}
      action={<LinkButton onClick={() => api.clearSection('property')}>Change</LinkButton>}
    />
  )
}

/** *2 jobs here · last: J-3207 Front walk pavers · Completed. A new request is still its own job.* */
function JobsHere({ property }: { property: KnownProperty }) {
  if (property.jobs === 0) return null
  const last = property.lastJob
  return (
    <div className="mt-2 text-[12px] leading-[1.5] text-text-muted">
      {jobsWord(property.jobs)} here
      {last && (
        <>
          {' · last: '}
          <b className="font-bold text-text">{last.reference} {last.title}</b>
          {` · ${humanize(last.status)}`}
        </>
      )}
      . A new request is still its own job.
    </div>
  )
}

/** A typed property, folded (Board 6): the **New** tag and **Edit**. */
function TypedCard({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  const zone = zoneName(property)
  return (
    <CardShell
      tile={(
        <PlaceMark seed={property.address.line1 || property.name} size={38} title={zone ?? undefined}
          colour={property.check?.zones.find((one) => one.id === property.zoneId)?.colour} />
      )}
      title={property.name.trim() || property.address.line1}
      tag={(
        <span className="inline-flex h-[19px] items-center rounded-tag bg-info-bg px-1.5 text-[10.5px] font-semibold text-info">
          New
        </span>
      )}
      sub={[addressLine(property.address, true), property.type, zone ? `${zone} zone` : null,
        branchName(property)].filter(Boolean).join(' · ')}
      action={<LinkButton onClick={() => api.openSection('property')}>Edit</LinkButton>}
    />
  )
}

/** A chosen contact's places, as radio cards (§5.4, Board 3 D). */
function PlacesOf({ contact }: { contact: KnownContact }) {
  const api = useRequestSheet()
  const property = api.state.property
  const chosenId = property.kind === 'chosen' ? property.property.id : null
  const adding = property.kind === 'new'

  return (
    <div role="radiogroup" aria-label={`${contact.firstName}’s properties`} className="flex flex-col gap-2">
      {contact.properties.map((place) => {
        const on = place.id === chosenId
        return (
          <button
            key={place.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => { if (!on) api.chooseProperty(place.id, { fromContact: true }) }}
            className={cn('flex items-center gap-3 rounded-[12px] border-[1.5px] px-3.5 py-[11px] text-left',
              on ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
                : 'border-line bg-surface hover:border-line-strong')}
          >
            <Radio on={on} />
            <PlaceMark seed={place.id} colour={place.zoneColour} size={32} />
            {/* 16px from the name to the line under it, as drawn (Board 3 D). */}
            <span className="flex min-w-0 flex-1 flex-col leading-[15px]">
              <b className="truncate text-[13.5px] font-bold leading-[16px] text-text">{place.name}</b>
              <span className="truncate text-[12px] text-text-muted">
                {[`${place.line1}, ${place.city}`, place.zoneName ? `${place.zoneName} zone` : null,
                  place.branchName].filter(Boolean).join(' · ')}
              </span>
            </span>
            {place.jobs > 0 && (
              <span className="whitespace-nowrap text-right text-[11.5px] text-text-faint">
                {jobsWord(place.jobs)}{place.jobs > 1 && place.lastJobTitle ? ` · last: ${place.lastJobTitle}` : ''}
              </span>
            )}
          </button>
        )
      })}
      {api.setup.canAddProperties && (
        <button
          type="button"
          role="radio"
          aria-checked={adding}
          onClick={() => {
            if (adding) return
            api.update((state) => ({ ...state, property: { kind: 'new', property: freshProperty() } }))
            api.requestFocus('address')
          }}
          className={cn('flex h-[38px] items-center gap-2.5 rounded-[12px] border-[1.5px] px-3.5 text-left',
            'text-[13px] font-semibold text-accent',
            adding ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
              : 'border-dashed border-accent-line')}
        >
          <IconPlus size={15} />
          A new property for {contact.firstName}
        </button>
      )}
    </div>
  )
}

function Radio({ on }: { on: boolean }) {
  return (
    <span aria-hidden className={cn('inline-block h-[18px] w-[18px] shrink-0 rounded-full bg-surface',
      on ? 'border-[5px] border-accent' : 'border-[1.5px] border-line-strong')} />
  )
}

// ---------------------------------------------------------------------------
// The address already a property (§7.2, Board 5 A) and the unit (A6)
// ---------------------------------------------------------------------------

function ExistingBand({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  const existing = existingShown(property)!
  const first = api.facts.contactFirstName
  const said = [existing.contactNames.join(', ') || null,
    existing.jobs > 0 ? jobsWord(existing.jobs) : null].filter(Boolean).join(' · ')

  return (
    <div>
      <div className="h-2.5" />
      <div role="alert" className={cn('flex items-start gap-[9px] rounded-card border border-amber-band-line',
        'bg-amber-band-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-amber-band-text')}>
        <span className="mt-px inline-flex shrink-0"><IconWarn size={15} /></span>
        <div>
          <b className="font-bold">{property.address.line1} is already {existing.name}</b>
          {existing.inScope
            ? ` ${said ? `(${said}). ` : ''}Use it — ${first ? `${first} is` : 'the contact is'} added as one of its contacts. Two properties can’t share an exact address.`
            : ' — in a branch you can’t see. Two properties can’t share an exact address.'}
        </div>
      </div>
      <div className="h-2.5" />
      <div className="flex flex-wrap items-center gap-2">
        {existing.inScope && (
          <button
            type="button"
            onClick={() => api.chooseProperty(existing.id, { inSection: true })}
            className={cn('inline-flex items-center justify-center gap-1.5 rounded-button border border-line',
              'bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:border-line-strong',
              api.phone ? 'h-11' : 'h-8')}
          >
            <IconPin size={14.5} />
            Use {existing.name}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            api.update((state) => changeProperty(state, (current) => ({
              ...current, anotherUnit: true,
              address: { ...current.address, needsUnit: true },
            })))
            api.requestFocus('unit')
          }}
          className={cn('inline-flex items-center rounded-button px-2 text-[12.5px] font-semibold',
            'text-text-muted hover:text-text', api.phone ? 'h-11' : 'h-8')}
        >
          It’s another unit or lot
        </button>
      </div>
      <div className="mt-2 text-[11.5px] leading-[1.45] text-text-muted">{UNIT_HINT}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Type, Name, Zone, Branch
// ---------------------------------------------------------------------------

function TypeChips({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  return (
    <div>
      <FieldLabel>Type</FieldLabel>
      <div role="radiogroup" aria-label="Type" className="flex flex-wrap gap-1.5">
        {PROPERTY_TYPES.map(({ value }) => {
          const on = property.type === value
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => api.update((state) => changeProperty(state, (current) =>
                ({ ...current, type: value })))}
              className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-chip border px-2.5',
                'text-[12px] font-semibold', api.phone ? 'h-[34px]' : 'h-8',
                on ? 'border-accent-line bg-accent-soft text-accent-dark'
                  : 'border-line bg-surface text-text hover:border-line-strong')}
            >
              {on && <CheckGlyph size={12} stroke={2.6} />}
              {value}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function NameField({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  const auto = property.nameIsAuto
  /* Back to the name the sheet makes; the effect above writes it in. */
  const restore = () => api.update((state) => changeProperty(state, (current) =>
    ({ ...current, nameIsAuto: true })))
  return (
    <div>
      <FieldLabel>Name</FieldLabel>
      <TextInput
        value={property.name}
        data-request-field="propertyName"
        size={api.phone ? 'touch' : 'default'}
        aria-label="Name"
        onChange={(event) => {
          const value = event.target.value
          api.update((state) => changeProperty(state, (current) =>
            ({ ...current, name: value, nameIsAuto: false })))
        }}
        /*
         * The *auto* tag stays in the field (Aaron, step 28): green while the
         * sheet is naming the place, grey once somebody has typed over it, and
         * a press puts the automatic name back. Not a tab stop — the field is
         * edited by clicking or tabbing into it, which is why there is no pencil.
         */
        suffix={(
          <button
            type="button"
            tabIndex={-1}
            aria-label={auto ? 'Named automatically' : 'Use the automatic name'}
            title={auto ? undefined : 'Use the automatic name'}
            onClick={auto ? undefined : restore}
            className={cn('inline-flex h-[18px] items-center gap-[3px] rounded-tag px-1.5 text-[10.5px] font-bold',
              auto ? 'cursor-default bg-accent-soft text-accent-dark'
                : 'bg-surface-alt text-text-muted hover:bg-accent-soft hover:text-accent-dark')}
          >
            <AutoGlyph />auto
          </button>
        )}
      />
      <div className={cn('mt-1 leading-[1.4] text-text-faint', api.phone ? 'text-[11.5px]' : 'text-[11px]')}>
        {auto
          ? api.phone ? 'from the name and the type' : 'from the name and the type — type over it to change it'
          : 'press auto to go back to the automatic name'}
      </div>
    </div>
  )
}

function ZoneAndBranch({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  const check = property.check
  const zones = check?.zones ?? []
  const branches = check?.branches ?? []
  const manyBranches = branches.length > 1
  const zone = zones.find((one) => one.id === property.zoneId) ?? null

  const zoneHint = !check ? null
    : property.zoneId && property.zoneId === check.zone?.id ? check.zoneHint
      : !property.zoneId && !check.zone ? check.zoneHint : null
  const branchHint = !check || !property.orgUnitId ? null
    : property.orgUnitId === check.branch?.id ? check.branch.why
      : zone && zone.branchId === property.orgUnitId ? `${zone.name}’s branch` : null

  const chooseZone = (id: string | null) => {
    const picked = zones.find((one) => one.id === id) ?? null
    api.update((state) => changeProperty(state, (current) => ({
      ...current,
      zoneId: picked?.id ?? null,
      /* A zone belongs to a branch: picking one picks its branch. */
      orgUnitId: picked ? picked.branchId : current.orgUnitId,
    })))
  }
  const chooseBranch = (id: string | null) => {
    api.update((state) => changeProperty(state, (current) => {
      const kept = zones.find((one) => one.id === current.zoneId)
      return {
        ...current,
        orgUnitId: id,
        /* Changing the branch empties a zone that belongs to another (§7.5). */
        zoneId: kept && kept.branchId === id ? current.zoneId : null,
      }
    }))
  }

  return (
    <div className={cn('grid grid-cols-2', api.phone ? 'gap-2.5' : 'gap-x-3.5 gap-y-3')}>
      <div className="min-w-0">
        <FieldLabel>Zone</FieldLabel>
        <Pick
          label="Zone"
          value={property.zoneId}
          placeholder="Pick a zone"
          options={zones.map((one) => ({
            value: one.id, label: one.name, ...(manyBranches ? { help: one.branchName } : {}),
          }))}
          onChange={chooseZone}
          clearable
        />
        {!api.phone && zoneHint && (
          <div className={cn('mt-1 text-[11px] leading-[1.4]',
            zoneHint.tone === 'green' ? 'text-accent-dark' : 'text-text-faint')}>
            {zoneHint.text}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <FieldLabel>Branch</FieldLabel>
        <Pick
          label="Branch"
          value={property.orgUnitId}
          placeholder="Pick a branch"
          options={branches.map((one) => ({ value: one.id, label: one.name }))}
          onChange={chooseBranch}
        />
        {!api.phone && branchHint && (
          <div className="mt-1 text-[11px] leading-[1.4] text-text-faint">{branchHint}</div>
        )}
      </div>
    </div>
  )
}

/**
 * One choice: the field kit's drop-down on a desktop, a bottom sheet with
 * 52 px rows on a phone (§16.3 — no native select, no listbox popover).
 */
function Pick({ label, value, options, placeholder, onChange, clearable }: {
  label: string
  value: string | null
  options: { value: string; label: string; help?: string }[]
  placeholder: string
  onChange: (value: string | null) => void
  clearable?: boolean
}) {
  const api = useRequestSheet()
  const [open, setOpen] = useState(false)
  const chosen = options.find((option) => option.value === value) ?? null

  if (!api.phone) {
    return (
      <KeySelect
        label={label}
        value={value}
        options={[...(clearable ? [{ value: '', label: 'No zone' }] : []),
          ...options.map((option) => ({ value: option.value, label: option.label, sub: option.help }))]}
        placeholder={placeholder}
        onChange={onChange}
      />
    )
  }

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={label}
        onClick={() => setOpen(true)}
        className={controlClass({ size: 'touch', className: 'justify-start text-left font-normal' })}
      >
        <span className={cn('min-w-0 flex-1 truncate', !chosen && 'text-text-faint')}>
          {chosen?.label ?? placeholder}
        </span>
        {/* A phone's picker opens a sheet, so it points onward, like Division (Board 14 C). */}
        <span className="shrink-0 text-text-muted"><IconChevronRight size={16} /></span>
      </button>
      <PanelSheet open={open} onClose={() => setOpen(false)} title={label} layer={70}>
        <div role="listbox" aria-label={label} className="flex flex-col">
          {[...(clearable ? [{ value: '', label: 'No zone' }] : []), ...options].map((option) => {
            const on = (option.value || null) === value
            return (
              <button
                key={option.value || 'none'}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(option.value || null); setOpen(false) }}
                className={cn('flex min-h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left',
                  on ? 'bg-green-band-bg' : 'bg-surface')}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="truncate text-[15px] font-semibold text-text">{option.label}</b>
                  {'help' in option && option.help && (
                    <span className="truncate text-[12.5px] text-text-muted">{option.help}</span>
                  )}
                </span>
                {on && <span className="shrink-0 text-accent"><IconCheck size={18} /></span>}
              </button>
            )
          })}
        </div>
      </PanelSheet>
    </>
  )
}

/** The *auto* chip's star and its little star, as Board 5 draws them — not the icon set's sparkles. */
function AutoGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={10} height={10} aria-hidden fill="none" stroke="currentColor"
      strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
    </svg>
  )
}
